/**
 * Grouped carousel paging.
 *
 * A group holds as many full slides as fit in the carousel, and at least one.
 * Groups do not overlap and do not copy slides. The last group may be shorter
 * than the others (a partial final page). Arrows, dots, keyboard arrows, and
 * dragging all move by one of those groups: dots select a group index, and
 * next / previous (pointer, keyboard, or drag) step that same index.
 *
 * The last group stays reachable. When it is shorter than the carousel, the
 * track aligns to the end so the trailing slides remain on screen.
 *
 * Resize keeps a valid group. Viewing the last group selects the new last
 * group. Any earlier group selects the group that contains that group's first
 * slide, then clamps into range. Slide order, identity, and links stay as authored.
 */

export type GroupMove = 'next' | 'previous' | 'dot';

export function normalizeSlidesPerView(perView: number): number {
  if (!Number.isFinite(perView) || perView < 1) return 1;
  return Math.floor(perView);
}

/**
 * How many full slides fit on the track. Pass the track width (Embla's
 * container), not the viewport. The track is one leading gutter wider than
 * the viewport (`-ml-4` against each slide's `pl-4`), and a fractional slide
 * is that track width divided by the column count. Dividing the viewport by
 * that slide width is short by one. A slide wider than the track still counts
 * as one.
 */
export function measureSlidesPerView(
  trackWidth: number,
  slideWidth: number,
): number {
  if (!(trackWidth > 0) || !(slideWidth > 0)) return 1;
  return Math.max(1, Math.floor(trackWidth / slideWidth + 0.01));
}

export function pageCount(slideCount: number, perView: number): number {
  if (slideCount <= 0) return 0;
  return Math.ceil(slideCount / normalizeSlidesPerView(perView));
}

export function groupStarts(slideCount: number, perView: number): number[] {
  const count = pageCount(slideCount, perView);
  const size = normalizeSlidesPerView(perView);
  return Array.from({ length: count }, (_, page) => page * size);
}

/** Slides in one group, including a shorter final group. */
export function slidesInGroup(
  slideCount: number,
  perView: number,
  page: number,
): number {
  const size = normalizeSlidesPerView(perView);
  const start = page * size;
  if (slideCount <= 0 || start >= slideCount || start < 0) return 0;
  return Math.min(size, slideCount - start);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Group to show after the number of slides that fit changes.
 * The final group stays the final group. Other groups follow their first slide.
 */
export function pageIndexAfterResize(input: {
  slideCount: number;
  previousPerView: number;
  previousPage: number;
  nextPerView: number;
}): number {
  const nextCount = pageCount(input.slideCount, input.nextPerView);
  if (nextCount === 0) return 0;

  const previousCount = pageCount(input.slideCount, input.previousPerView);
  const previousLast = Math.max(0, previousCount - 1);
  const previousPage = clamp(input.previousPage, 0, previousLast);

  if (previousCount > 0 && previousPage >= previousCount - 1) {
    return nextCount - 1;
  }

  const anchor = previousPage * normalizeSlidesPerView(input.previousPerView);
  const nextPage = Math.floor(
    anchor / normalizeSlidesPerView(input.nextPerView),
  );
  return clamp(nextPage, 0, nextCount - 1);
}

/** Shared target for a next arrow, previous arrow, keyboard arrow, drag, or dot. */
export function moveGroup(
  page: number,
  pageTotal: number,
  move: GroupMove,
  dotIndex = 0,
): number {
  if (pageTotal <= 0) return 0;
  if (move === 'dot') return clamp(dotIndex, 0, pageTotal - 1);
  const delta = move === 'next' ? 1 : -1;
  return clamp(page + delta, 0, pageTotal - 1);
}
